import { useState, useEffect, useCallback } from "react";
import api from "../api";
import { marca, nomePdf } from "../marca";
import { useTheme } from "../components/Theme";
import ModalCategorias from "./ModalCategorias";
import DashboardFinanceiro from "./DashboardFinanceiro";
import ModalContas from "./ModalContas";
import RelatorioFinanceiro from "./RelatorioFinanceiro";

const ORANGE = "#FF6B2B";

const STATUS = {
  pendente:  { label: "Pendente",   color: "#E8912A" },
  pago:      { label: "Pago",       color: "#1D9E75" },
  vencido:   { label: "Atrasado",   color: "#E24B4A" },
  cancelado: { label: "Cancelado",  color: "#8E8E93" },
};

const FORMAS = ["pix","boleto","transferencia","cartao","dinheiro","outro"];
const FORMA_LABEL = { pix:"PIX", boleto:"Boleto", transferencia:"Transferência", cartao:"Cartão", dinheiro:"Dinheiro", outro:"Outro" };
const CATEGORIAS_SAIDA  = ["Salários","Fornecedores","Ferramentas/Software","Marketing","Impostos","Aluguel","Outros"];
const CATEGORIAS_ENTRADA = ["Mensalidade","Projeto","Consultoria","Outros"];

function fmt(v) { return `R$${Number(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}` ; }
function fmtData(d) { if(!d) return "—"; const raw = new Date(d); const dt = new Date(raw.getTime() + raw.getTimezoneOffset()*60000); return dt.toLocaleDateString("pt-BR"); }

// Primeiro e último dia do mês atual
function mesAtual() {
  const hoje = new Date();
  const de  = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0,10);
  const ate = new Date(hoje.getFullYear(), hoje.getMonth()+1, 0).toISOString().slice(0,10);
  return { de, ate };
}

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.pendente;
  return <span style={{ display:"inline-flex", alignItems:"center", gap:6, color:s.color, fontSize:12, fontWeight:600, whiteSpace:"nowrap" }}><span style={{ width:7, height:7, borderRadius:"50%", background:s.color, flexShrink:0 }}/>{s.label}</span>;
}

function MiniBar({ entradas, saidas, max }) {
  const { theme } = useTheme();
  return (
    <div style={{ display:"flex", gap:3, alignItems:"flex-end", height:50 }}>
      {[{v:entradas,c:"#10b981"},{v:saidas,c:"#ef4444"}].map(({v,c},i)=>(
        <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
          <div style={{ background:c, borderRadius:"4px 4px 0 0", height:`${max?Math.max((v/max)*100,4):4}%`, minHeight:4, transition:"height .4s" }}/>
        </div>
      ))}
    </div>
  );
}

// Modal lançamento
function ModalLancamento({ lanc, tipo: tipoDefault, onClose, onSave, clientes, categorias = [], contas = [] }) {
  const { theme } = useTheme();
  const isNew = !lanc?._id;
  const prep = (l) => l ? { ...l, dataEmissao: l.dataEmissao ? String(l.dataEmissao).slice(0,10) : "", dataVencimento: l.dataVencimento ? String(l.dataVencimento).slice(0,10) : "", dataPagamento: l.dataPagamento ? String(l.dataPagamento).slice(0,10) : "" } : null;
  const [form, setForm] = useState(prep(lanc) || {
    tipo: tipoDefault || "entrada",
    subtipo: tipoDefault === "entrada" ? "nota_emitida" : "nota_recebida",
    descricao: "", valor: "", status: "pendente", formaPagto: "pix",
    dataEmissao: new Date().toISOString().slice(0,10),
    dataVencimento: "", dataPagamento: "",
    clienteNome: "", numeroNota: "",
    categoria: tipoDefault === "entrada" ? "Mensalidade" : "Outros",
    observacoes: "",
    recorrente: false, recModo: "meses", recMeses: 12, recAte: "",
  });
  const [load, setLoad] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const inp = { width:"100%", boxSizing:"border-box", background:theme.bgInput, border:`1.5px solid ${theme.border}`, borderRadius:10, padding:"9px 12px", color:theme.text, fontSize:13, outline:"none", colorScheme:theme.name, fontFamily:"'Inter',sans-serif", transition:"border-color .15s" };

  const salvar = async () => {
    if (!form.descricao.trim() || !form.valor) return alert("Descrição e valor são obrigatórios");
    setLoad(true);
    try { const payload = { ...form }; if (isNew && form.recorrente) payload.recorrencia = { ativo: true, meses: form.recModo === "meses" ? (Number(form.recMeses) || 1) : null, ate: form.recModo === "ate" ? form.recAte : null }; await onSave(payload); }
    catch(e) { alert(e.response?.data?.erro || "Erro ao salvar"); }
    finally { setLoad(false); }
  };

  const isEntrada = form.tipo === "entrada";
  const cats = categorias.filter(c => c.tipo === (isEntrada ? "entrada" : "saida")).map(c => c.nome);

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.75)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:20, padding:32, width:"100%", maxWidth:520, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 80px rgba(0,0,0,.6)" }} onClick={e=>e.stopPropagation()}>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
          <div>
            <div style={{ fontSize:11, color:isEntrada?"#10b981":"#ef4444", fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>
              {isEntrada ? "💰 NOTA EMITIDA / ENTRADA" : "💸 NOTA RECEBIDA / SAÍDA"}
            </div>
            <h2 style={{ margin:0, fontSize:18, fontWeight:700, color:theme.text }}>{isNew?"Novo lançamento":"Editar lançamento"}</h2>
          </div>
          <button onClick={onClose} style={{ background:theme.bgInput, border:"none", borderRadius:8, width:32, height:32, color:theme.textMuted, fontSize:18, cursor:"pointer" }}>×</button>
        </div>

        {/* Tipo */}
        <div style={{ display:"flex", gap:8, marginBottom:20 }}>
          {[["entrada","💰 Entrada / A receber","#10b981"],["saida","💸 Saída / A pagar","#ef4444"]].map(([val,label,cor])=>(
            <button key={val} onClick={()=>{ set("tipo",val); set("subtipo",val==="entrada"?"nota_emitida":"nota_recebida"); set("categoria",val==="entrada"?"Mensalidade":"Outros"); }}
              style={{ flex:1, padding:"9px 0", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer", border:`2px solid ${form.tipo===val?cor:theme.border}`, background:form.tipo===val?cor+"18":"transparent", color:form.tipo===val?cor:theme.textMuted, transition:"all .15s" }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
          <div style={{ gridColumn:"span 2" }}>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Descrição *</label>
            <input value={form.descricao} onChange={e=>set("descricao",e.target.value)} style={inp} placeholder="Ex: Mensalidade cliente X — Junho/2026"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Valor (R$) *</label>
            <input type="number" value={form.valor} onChange={e=>set("valor",e.target.value)} style={inp} placeholder="0,00"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Nº Nota / Referência</label>
            <input value={form.numeroNota||""} onChange={e=>set("numeroNota",e.target.value)} style={inp} placeholder="001/2026"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 }}>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Status</label>
            <select value={form.status} onChange={e=>set("status",e.target.value)} style={{ ...inp, appearance:"auto" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}>
              {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Forma de pagamento</label>
            <select value={form.formaPagto} onChange={e=>set("formaPagto",e.target.value)} style={{ ...inp, appearance:"auto" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}>
              {FORMAS.map(f=><option key={f} value={f}>{FORMA_LABEL[f]}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Categoria</label>
            <select value={form.categoria||""} onChange={e=>set("categoria",e.target.value)} style={{ ...inp, appearance:"auto" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}>
              {cats.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginBottom:14 }}><label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>🏦 Banco / Conta</label><select value={form.banco||""} onChange={e=>set("banco",e.target.value)} style={{ ...inp, appearance:"auto" }} onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}><option value="">— Selecione —</option>{contas.map(c=><option key={c._id} value={c.nome}>{c.nome}{c.agencia?` (Ag. ${c.agencia})`:""}</option>)}</select></div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 }}>
          {[["Data emissão","dataEmissao"],["Data vencimento","dataVencimento"],["Data pagamento","dataPagamento"]].map(([label,key])=>(
            <div key={key}>
              <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>{label}</label>
              <input type="date" value={form[key]||""} onChange={e=>set(key,e.target.value)} style={inp}
                onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            </div>
          ))}
        </div>

        {isNew && (
          <div style={{ marginBottom:16, padding:"12px 14px", border:`1px solid ${theme.border}`, borderRadius:10, background:theme.bgInput }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:13, fontWeight:600, color:theme.text }}>
              <input type="checkbox" checked={!!form.recorrente} onChange={e=>set("recorrente", e.target.checked)} style={{ width:16, height:16, accentColor:ORANGE }}/>
              Lançamento recorrente (repetir todo mês)
            </label>
            {form.recorrente && (
              <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:10, flexWrap:"wrap" }}>
                <select value={form.recModo} onChange={e=>set("recModo", e.target.value)} style={{ ...inp, width:"auto", appearance:"auto" }}>
                  <option value="meses">Repetir por X meses</option>
                  <option value="ate">Repetir até uma data</option>
                </select>
                {form.recModo==="meses"
                  ? <input type="number" min="2" max="120" value={form.recMeses} onChange={e=>set("recMeses", e.target.value)} style={{ ...inp, width:90 }}/>
                  : <input type="date" value={form.recAte||""} onChange={e=>set("recAte", e.target.value)} style={{ ...inp, width:"auto" }}/>}
                <span style={{ fontSize:11, color:theme.textMuted }}>1 lançamento por mês, a partir do vencimento (ou emissão)</span>
              </div>
            )}
          </div>
        )}

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:20 }}>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Cliente (opcional)</label>
            <input value={form.clienteNome||""} onChange={e=>set("clienteNome",e.target.value)} style={inp} placeholder="Nome do cliente"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Observações</label>
            <input value={form.observacoes||""} onChange={e=>set("observacoes",e.target.value)} style={inp} placeholder="Notas adicionais"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
        </div>

        <div style={{ display:"flex", gap:10 }}>
          <button onClick={salvar} disabled={load} style={{ flex:1, background:`linear-gradient(135deg,${isEntrada?"#10b981":"#ef4444"},${isEntrada?"#34d399":"#f87171"})`, border:"none", borderRadius:12, padding:"13px 0", color:"#fff", fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'Inter',sans-serif", opacity:load?.7:1 }}>
            {load?"Salvando...":"Salvar lançamento"}
          </button>
          {!isNew && <button onClick={()=>onSave({...form,_deletar:true})} style={{ background:"#ef444418", border:"1px solid #ef444433", borderRadius:12, padding:"13px 18px", color:"#ef4444", fontWeight:600, fontSize:14, cursor:"pointer" }}>Excluir</button>}
        </div>
      </div>
    </div>
  );
}

// Tabela de lançamentos
function TabelaLancamentos({ lancamentos, onEdit, onPagar, tipo }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const COL_CAMPO = { 0:"descricao", 1:"numeroNota", 2:"clienteNome", 3:"categoria", 5:"dataEmissao", 6:"dataVencimento", 7:"dataPagamento", 8:"valor", 9:"status" };
  const COL_TIPO  = { 8:"num", 5:"data", 6:"data", 7:"data" };
  const clicarOrdenar = (i) => {
    if (!COL_CAMPO[i]) return;
    if (sortCol===i) setSortDir(d => d==="asc"?"desc":"asc");
    else { setSortCol(i); setSortDir("asc"); }
  };
  const lancamentosOrdenados = (() => {
    if (sortCol===null || !COL_CAMPO[sortCol]) return lancamentos;
    const campo = COL_CAMPO[sortCol]; const tp = COL_TIPO[sortCol]; const dir = sortDir==="asc"?1:-1;
    return [...lancamentos].sort((a,b) => {
      let va=a[campo], vb=b[campo];
      if (tp==="num")  { va=Number(va||0); vb=Number(vb||0); return (va-vb)*dir; }
      if (tp==="data") { va=va?new Date(va).getTime():0; vb=vb?new Date(vb).getTime():0; return (va-vb)*dir; }
      return String(va||"").localeCompare(String(vb||""), "pt-BR")*dir;
    });
  })();
  const { theme } = useTheme();
  const isEntrada = tipo === "entrada";
  const cor = isEntrada ? "#10b981" : "#ef4444";

  if (lancamentos.length === 0) return (
    <div style={{ textAlign:"center", padding:"40px 0", color:theme.textMuted }}>
      <div style={{ fontSize:32, marginBottom:10 }}>{isEntrada?"💰":"💸"}</div>
      <div style={{ fontSize:14, fontWeight:600, color:theme.text, marginBottom:4 }}>Nenhum lançamento encontrado</div>
      <div style={{ fontSize:13 }}>Ajuste o período ou adicione um novo lançamento</div>
    </div>
  );

  const total = lancamentos.reduce((a,l)=>a+Number(l.valor||0),0);
  const pago  = lancamentos.filter(l=>l.status==="pago").reduce((a,l)=>a+Number(l.valor||0),0);

  return (
    <div>
      <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:theme.bgInput }}>
              {["Descrição","Nº Nota","Cliente","Categoria","Forma Pgto","Emissão","Vencimento","Pagamento","Valor","Status",""].map((h,i)=>(
                <th key={i} onClick={()=>clicarOrdenar(i)} style={{ padding:"10px 14px", fontSize:11, color:sortCol===i?ORANGE:theme.textMuted, fontWeight:600, letterSpacing:".05em", textTransform:"uppercase", textAlign:i>=8?"center":"left", borderRight:`1px solid ${theme.border}`, whiteSpace:"nowrap", cursor:COL_CAMPO[i]?"pointer":"default", userSelect:"none" }}>{h}{sortCol===i?(sortDir==="asc"?" ↑":" ↓"):""}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lancamentosOrdenados.map(l=>{
              const vencido = l.dataVencimento && String(l.dataVencimento).slice(0,10) < new Date().toLocaleDateString("en-CA") && l.status === "pendente";
              return (
                <tr key={l._id} style={{ borderBottom:`1px solid ${theme.border}`, cursor:"pointer" }}
                  onMouseEnter={e=>e.currentTarget.style.background=theme.bgInput}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                  onClick={()=>onEdit(l)}>
                  <td style={{ padding:"11px 14px", fontSize:13, fontWeight:600, color:theme.text, maxWidth:200 }}>
                    <div style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.descricao}</div>
                    {l.observacoes && <div style={{ fontSize:11, color:theme.textMuted, marginTop:2 }}>{l.observacoes}</div>}
                  </td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textMuted }}>{l.numeroNota||"—"}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textSub }}>{l.clienteNome||"—"}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textMuted }}>{l.categoria||"—"}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textMuted }}>{FORMA_LABEL[l.formaPagto]||"—"}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textMuted }}>{fmtData(l.dataEmissao)}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:vencido?"#ef4444":theme.textMuted, fontWeight:vencido?700:400 }}>
                    {fmtData(l.dataVencimento)}{vencido?" ⚠️":""}
                  </td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textMuted }}>{fmtData(l.dataPagamento)}</td>
                  <td style={{ padding:"11px 14px", fontSize:14, fontWeight:800, color:cor, textAlign:"right" }}>{fmt(l.valor)}</td>
                  <td style={{ padding:"11px 14px", textAlign:"center" }}><StatusBadge status={vencido ? "vencido" : l.status}/></td>
                  <td style={{ padding:"11px 10px", textAlign:"center" }}>
                    {l.status === "pendente" && (
                      <button onClick={e=>{ e.stopPropagation(); onPagar(l._id); }}
                        style={{ background:cor+"22", border:`1px solid ${cor}44`, borderRadius:6, padding:"4px 10px", color:cor, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                        {isEntrada?"✓ Receber":"✓ Pagar"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop:`2px solid ${theme.border}`, background:theme.bgInput }}>
              <td colSpan={8} style={{ padding:"11px 14px", fontSize:12, fontWeight:700, color:theme.textMuted, textAlign:"right", textTransform:"uppercase", letterSpacing:".05em" }}>Total ({lancamentosOrdenados.length})</td>
              <td style={{ padding:"11px 14px", fontSize:14, fontWeight:800, color:cor, textAlign:"right" }}>{fmt(lancamentosOrdenados.reduce((a,l)=>a+Number(l.valor||0),0))}</td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// DRE
function carregarScriptFin(src) {
  return new Promise((res, rej) => {
    const existente = document.querySelector(`script[src="${src}"]`);
    if (existente) {
      if (existente.dataset.carregado === "1") return res();
      existente.addEventListener("load", () => res());
      existente.addEventListener("error", rej);
      return;
    }
    const el = document.createElement("script");
    el.src = src;
    el.onload = () => { el.dataset.carregado = "1"; res(); };
    el.onerror = rej;
    document.body.appendChild(el);
  });
}

function DREMensal({ de, ate, theme }) {
  const [lancs, setLancs] = useState([]);
  const [load, setLoad] = useState(true);
  const [verComp, setVerComp] = useState(false);
  useEffect(() => {
    let vivo = true; setLoad(true);
    Promise.all([
      api.get(`/financeiro?de=${de}&ate=${ate}&campoData=dataEmissao`),
      api.get(`/financeiro?de=${de}&ate=${ate}&campoData=dataPagamento`),
    ]).then(([a,b]) => {
      if (!vivo) return;
      const map = {};
      [...(a.data.lancamentos||[]), ...(b.data.lancamentos||[])].forEach(l => { map[l._id] = l; });
      setLancs(Object.values(map));
    }).catch(()=>{}).finally(()=> { if (vivo) setLoad(false); });
    return () => { vivo = false; };
  }, [de, ate]);

  const noMes = (dref, ini, fim) => { if(!dref) return false; const d = String(dref).slice(0,10); return d >= ini && d <= fim; };
  const comp = lancs.filter(l => l.status !== "cancelado" && noMes(l.dataEmissao, de, ate));
  const cax  = lancs.filter(l => l.status === "pago" && noMes(l.dataPagamento, de, ate));
  const mapCat = (arr, tipo) => { const m={}; arr.filter(l=>l.tipo===tipo).forEach(l=>{ const c=l.categoria||"Sem categoria"; m[c]=(m[c]||0)+Number(l.valor||0); }); return m; };
  const recC=mapCat(comp,"entrada"), recX=mapCat(cax,"entrada"), despC=mapCat(comp,"saida"), despX=mapCat(cax,"saida");
  const tot = (m) => Object.values(m).reduce((a,v)=>a+v,0);
  const totRecC=tot(recC), totRecX=tot(recX), totDespC=tot(despC), totDespX=tot(despX);
  const resC=totRecC-totDespC, resX=totRecX-totDespX;
  const keys = (a,b) => [...new Set([...Object.keys(a),...Object.keys(b)])].sort((k1,k2)=>((b[k2]||0)+(a[k2]||0))-((b[k1]||0)+(a[k1]||0)));
  const kRec=keys(recC,recX), kDesp=keys(despC,despX);
  const mesLabel = new Date(String(de)+"T12:00:00").toLocaleDateString("pt-BR",{month:"long",year:"numeric"});

  const gerarPdfDRE = async () => {
    try {
      await carregarScriptFin("/vendor/jspdf.umd.min.js");
      await carregarScriptFin("/vendor/jspdf.plugin.autotable.min.js");
      let { jsPDF } = window.jspdf;
      let doc = new jsPDF({ orientation: "landscape" });
      if (typeof doc.autoTable !== "function") {
        await new Promise((r,j)=>{ const el=document.createElement("script"); el.src="/vendor/jspdf.plugin.autotable.min.js?v="+Date.now(); el.onload=r; el.onerror=j; document.body.appendChild(el); });
        jsPDF = window.jspdf.jsPDF; doc = new jsPDF({ orientation: "landscape" });
      }
      const W = doc.internal.pageSize.getWidth();
      const M = 14;
      const titulo = mesLabel.charAt(0).toUpperCase() + mesLabel.slice(1);
      const G=[29,158,117], R=[226,75,74], LARANJA=[238,106,26], ESCURO=[38,38,40], CINZA=[120,120,120], TOT=[240,240,242], SEC=[248,238,229];
      // Cabecalho
      doc.setFontSize(8); doc.setTextColor(...CINZA); doc.setFont(undefined,"normal");
      doc.text(nomePdf(await marca()), M, 11);
      doc.setFontSize(15); doc.setTextColor(...LARANJA); doc.setFont(undefined,"bold");
      doc.text("Fechamento - DRE " + titulo, M, 18);
      doc.setFontSize(8); doc.setTextColor(...CINZA); doc.setFont(undefined,"normal");
      doc.text("Competencia = emissao (resultado)     Caixa = o que entrou / saiu no mes", M, 23);
      // 3 cards Entrou / Saiu / Saldo (caixa)
      const cardY=27, cardH=16, gap=6, cardW=(W-2*M-2*gap)/3;
      [["ENTROU NO MES", totRecX, G],["SAIU NO MES", totDespX, R],["SALDO DO MES", resX, resX>=0?G:R]].forEach(([lbl,val,cor],i)=>{
        const x=M+i*(cardW+gap);
        doc.setFillColor(248,249,250); doc.setDrawColor(230,230,232);
        doc.roundedRect(x, cardY, cardW, cardH, 1.8, 1.8, "FD");
        doc.setFillColor(...cor); doc.rect(x, cardY, 1.8, cardH, "F");
        doc.setFontSize(7); doc.setTextColor(...CINZA); doc.setFont(undefined,"bold");
        doc.text(lbl, x+7, cardY+6);
        doc.setFontSize(13.5); doc.setTextColor(...cor);
        doc.text(fmt(val), x+7, cardY+12.5);
      });
      // Tabela (segue o toggle: por padrao so Caixa)
      const nSpan = verComp ? 3 : 2;
      const rec3 = (label, c, x, st) => verComp ? [{content:label,styles:st},{content:fmt(c),styles:st},{content:fmt(x),styles:st}] : [{content:label,styles:st},{content:fmt(x),styles:st}];
      const body=[];
      body.push([{content:"RECEITAS",colSpan:nSpan,styles:{fontStyle:"bold",fillColor:SEC,textColor:LARANJA}}]);
      kRec.forEach(c=>{ const r=[c]; if(verComp) r.push(fmt(recC[c]||0)); r.push(fmt(recX[c]||0)); body.push(r); });
      body.push(rec3("Total de Receitas", totRecC, totRecX, {fontStyle:"bold",fillColor:TOT,textColor:G}));
      body.push([{content:"DESPESAS",colSpan:nSpan,styles:{fontStyle:"bold",fillColor:SEC,textColor:LARANJA}}]);
      kDesp.forEach(c=>{ const r=[c]; if(verComp) r.push(fmt(despC[c]||0)); r.push(fmt(despX[c]||0)); body.push(r); });
      body.push(rec3("Total de Despesas", totDespC, totDespX, {fontStyle:"bold",fillColor:TOT,textColor:R}));
      body.push(rec3("= RESULTADO DO MES", resC, resX, {fontStyle:"bold",fillColor:ESCURO,textColor:[255,255,255]}));
      const mgC=(totRecC?Math.round(resC/totRecC*100):0)+"%", mgX=(totRecX?Math.round(resX/totRecX*100):0)+"%";
      body.push(verComp ? [{content:"Margem",styles:{textColor:CINZA}},{content:mgC,styles:{textColor:CINZA}},{content:mgX,styles:{textColor:CINZA}}] : [{content:"Margem",styles:{textColor:CINZA}},{content:mgX,styles:{textColor:CINZA}}]);
      doc.autoTable({
        head: verComp ? [["", "COMPETENCIA", "CAIXA"]] : [["", "CAIXA"]], body, startY:cardY+cardH+6, theme:"grid",
        styles:{ fontSize:8, cellPadding:{top:1.5,right:3,bottom:1.5,left:3}, lineColor:[236,236,238], lineWidth:0.1, textColor:[55,55,60] },
        headStyles:{ fillColor:LARANJA, textColor:[255,255,255], fontStyle:"bold", halign:"right", fontSize:8 },
        alternateRowStyles:{ fillColor:[250,250,251] },
        columnStyles: verComp ? { 0:{halign:"left"}, 1:{halign:"right", cellWidth:62}, 2:{halign:"right", cellWidth:62} } : { 0:{halign:"left"}, 1:{halign:"right", cellWidth:70} },
        margin:{ left:M, right:M },
        didParseCell:(d)=>{ if(d.section==="head" && d.column.index===0) d.cell.styles.halign="left"; },
      });
      doc.save("DRE-" + String(de).slice(0,7) + ".pdf");
    } catch(e){ alert("Erro ao gerar PDF: " + e.message); }
  };

  const cel  = { padding:"9px 16px", fontSize:13, textAlign:"right", fontFamily:"'Inter',sans-serif" };
  const celL = { padding:"9px 16px", fontSize:13, textAlign:"left", color:theme.textSub };
  const sec = (t) => (<tr key={t}><td colSpan={verComp?3:2} style={{ padding:"10px 16px", fontSize:11, fontWeight:800, letterSpacing:.5, textTransform:"uppercase", color:theme.textMuted, background:theme.bgInput }}>{t}</td></tr>);
  const linha = (label, c, x, opts={}) => (
    <tr key={"l-"+label} style={{ borderTop:`1px solid ${theme.border}`, background: opts.destaque ? theme.bgInput : "transparent" }}>
      <td style={{ ...celL, fontWeight: opts.destaque?800:400, color: opts.destaque?theme.text:theme.textSub, width: verComp?"auto":"100%" }}>{label}</td>
      {verComp && <td style={{ ...cel, fontWeight: opts.destaque?800:600, color: opts.corC || theme.text }}>{fmt(c)}</td>}
      <td style={{ ...cel, fontWeight: opts.destaque?800:600, color: opts.corX || theme.text, width: verComp?"auto":180 }}>{fmt(x)}</td>
    </tr>
  );

  if (load) return <div style={{ textAlign:"center", padding:40, color:theme.textMuted }}>Carregando DRE...</div>;

  return (
    <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, overflow:"hidden" }}>
      <div style={{ padding:"14px 20px", borderBottom:`1px solid ${theme.border}`, background:theme.bgInput, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
        <span style={{ fontSize:13, fontWeight:700, color:theme.text, textTransform:"capitalize" }}>📋 DRE — {mesLabel}</span>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}><span style={{ fontSize:11, color:theme.textMuted }}>{verComp ? "Competência = emissão · Caixa = pago no mês" : "Caixa = o que entrou / saiu no mês"}</span><button onClick={()=>setVerComp(v=>!v)} style={{ padding:"6px 12px", fontSize:12, fontWeight:600, color:theme.textMuted, background:"transparent", border:`1px solid ${theme.border}`, borderRadius:6, cursor:"pointer" }}>{verComp ? "Ocultar competência" : "Ver competência"}</button><button onClick={gerarPdfDRE} style={{ padding:"6px 12px", fontSize:12, fontWeight:700, color:"#fff", background:"#EE6A1A", border:"none", borderRadius:6, cursor:"pointer" }}>Exportar PDF</button></div>
      </div>
      <div style={{ display:"flex", gap:12, padding:"16px 20px", borderBottom:`1px solid ${theme.border}`, flexWrap:"wrap" }}>
        {[
          ["Entrou no mês", totRecX, "#1D9E75", "recebido (caixa)"],
          ["Saiu no mês", totDespX, "#E24B4A", "pago (caixa)"],
          ["Saldo do mês", resX, resX>=0?"#1D9E75":"#E24B4A", "entrou − saiu"],
        ].map(([lbl,val,cor,sub])=>(
          <div key={lbl} style={{ flex:"1 1 160px", minWidth:160, background:theme.bgInput, borderRadius:10, padding:"12px 16px", borderLeft:`3px solid ${cor}` }}>
            <div style={{ fontSize:11, color:theme.textMuted, fontWeight:600, textTransform:"uppercase", letterSpacing:.3 }}>{lbl}</div>
            <div style={{ fontSize:26, fontWeight:800, color:cor, marginTop:4, fontFamily:"'Inter',sans-serif" }}>{fmt(val)}</div>
            <div style={{ fontSize:10.5, color:theme.textMuted, marginTop:2 }}>{sub}</div>
          </div>
        ))}
      </div>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr>
            <th style={{ ...celL, fontSize:11, textTransform:"uppercase", color:theme.textMuted, fontWeight:700, width: verComp?"auto":"100%" }}></th>
            {verComp && <th style={{ ...cel, fontSize:11, textTransform:"uppercase", color:theme.textMuted, fontWeight:700 }}>Competência</th>}
            <th style={{ ...cel, fontSize:11, textTransform:"uppercase", color:theme.textMuted, fontWeight:700, width: verComp?"auto":180 }}>Caixa</th>
          </tr>
        </thead>
        <tbody>
          {sec("Receitas")}
          {kRec.map(c => linha(c, recC[c]||0, recX[c]||0, { corC:"#10b981", corX:"#10b981" }))}
          {linha("Total de Receitas", totRecC, totRecX, { destaque:true, corC:"#10b981", corX:"#10b981" })}
          {sec("Despesas")}
          {kDesp.map(c => linha(c, despC[c]||0, despX[c]||0, { corC:"#ef4444", corX:"#ef4444" }))}
          {linha("Total de Despesas", totDespC, totDespX, { destaque:true, corC:"#ef4444", corX:"#ef4444" })}
          {linha("= Resultado do mês", resC, resX, { destaque:true, corC: resC>=0?"#10b981":"#ef4444", corX: resX>=0?"#10b981":"#ef4444" })}
          <tr style={{ borderTop:`1px solid ${theme.border}` }}>
            <td style={{ ...celL }}>Margem</td>
            <td style={{ ...cel, color:theme.textMuted }}>{totRecC? Math.round((resC/totRecC)*100):0}%</td>
            <td style={{ ...cel, color:theme.textMuted }}>{totRecX? Math.round((resX/totRecX)*100):0}%</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function DRE({ resumo, theme }) {
  if (!resumo) return null;
  const linhas = [
    ["Receita bruta", resumo.totalEntradas, "#10b981", false],
    ["(-) Deduções / Cancelamentos", 0, theme.textMuted, false],
    ["= Receita líquida", resumo.totalEntradas, "#10b981", true],
    ["(-) Despesas operacionais", resumo.totalSaidas, "#ef4444", false],
    ["= Resultado operacional", resumo.totalEntradas - resumo.totalSaidas, resumo.totalEntradas - resumo.totalSaidas >= 0 ? "#10b981" : "#ef4444", true],
    ["Valores já recebidos", resumo.totalRecebido, "#10b981", false],
    ["Valores já pagos", resumo.totalPago, "#ef4444", false],
    ["= Lucro líquido realizado", resumo.lucroLiquido, resumo.lucroLiquido >= 0 ? "#10b981" : "#ef4444", true],
  ];
  return (
    <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, overflow:"hidden" }}>
      <div style={{ padding:"14px 20px", borderBottom:`1px solid ${theme.border}`, background:theme.bgInput }}>
        <span style={{ fontSize:13, fontWeight:700, color:theme.text }}>📋 DRE — Demonstração do Resultado</span>
      </div>
      {linhas.map(([label, valor, cor, destaque], i) => (
        <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"11px 20px", borderBottom:`1px solid ${theme.border}`, background:destaque?cor+"08":"transparent" }}>
          <span style={{ fontSize:13, fontWeight:destaque?700:400, color:destaque?theme.text:theme.textSub }}>{label}</span>
          <span style={{ fontSize:14, fontWeight:800, color:cor, fontFamily:"'Inter',sans-serif" }}>{fmt(valor)}</span>
        </div>
      ))}
    </div>
  );
}

// Gráfico de barras simples
function GraficoFluxo({ dados, theme }) {
  if (!dados?.length) return null;
  const maxVal = Math.max(...dados.map(d => Math.max(d.entradas, d.saidas)), 1);
  return (
    <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:20 }}>
      <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:16 }}>📊 Fluxo de Caixa — Últimos 6 meses</div>
      <div style={{ display:"flex", gap:4, alignItems:"flex-end", height:120, marginBottom:8 }}>
        {dados.map((d,i) => {
          const hE = Math.max((d.entradas/maxVal)*100, 2);
          const hS = Math.max((d.saidas/maxVal)*100, 2);
          return (
            <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
              <div style={{ display:"flex", gap:2, alignItems:"flex-end", width:"100%", height:100 }}>
                <div style={{ flex:1, background:"#10b981", borderRadius:"4px 4px 0 0", height:`${hE}%`, minHeight:3, transition:"height .4s" }} title={`Entradas: ${fmt(d.entradas)}`}/>
                <div style={{ flex:1, background:"#ef4444", borderRadius:"4px 4px 0 0", height:`${hS}%`, minHeight:3, transition:"height .4s" }} title={`Saídas: ${fmt(d.saidas)}`}/>
              </div>
              <div style={{ fontSize:10, color:theme.textMuted, textAlign:"center" }}>{d.label}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:16, justifyContent:"center" }}>
        {[["#10b981","Entradas"],["#ef4444","Saídas"]].map(([cor,label])=>(
          <div key={label} style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:theme.textMuted }}>
            <div style={{ width:10, height:10, borderRadius:2, background:cor }}/>
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Financeiro({ currentUser }) {
  const { theme } = useTheme();
  const { de: deInit, ate: ateInit } = mesAtual();
  const [aba, setAba]             = useState("entradas");
  const [lancamentos, setLancs]   = useState([]);
  const [resumo, setResumo]       = useState(null);
  const [saldoInicial, setSaldoInicial] = useState(0);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [modalCategorias, setModalCategorias] = useState(false);
  const [modalContas, setModalContas] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [contas, setContas]       = useState([]);
  const [tipoModal, setTipoModal] = useState("entrada");
  const [de, setDe]               = useState(deInit);
  const [ate, setAte]             = useState(ateInit);
  const [campoData, setCampoData] = useState("dataEmissao");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [semana, setSemana] = useState([]);
  const mesIni = (a,m) => `${a}-${String(m+1).padStart(2,"0")}-01`;
  const mesFim = (a,m) => `${a}-${String(m+1).padStart(2,"0")}-${String(new Date(a,m+1,0).getDate()).padStart(2,"0")}`;
  const irMes = (a,m) => { setDe(mesIni(a,m)); setAte(mesFim(a,m)); };
  const passoMes = (delta) => { const b = new Date((de||new Date().toISOString().slice(0,10))+"T12:00:00"); const nd = new Date(b.getFullYear(), b.getMonth()+delta, 1); irMes(nd.getFullYear(), nd.getMonth()); };
  const rotuloMes = new Date((de||new Date().toISOString().slice(0,10))+"T12:00:00").toLocaleDateString("pt-BR",{month:"long",year:"numeric"});

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [lancsRes, resumoRes, catsRes, contasRes, saldoRes] = await Promise.all([
        api.get(`/financeiro?de=${de}&ate=${ate}&campoData=${campoData}`),
        api.get(`/financeiro/resumo?de=${de}&ate=${ate}&campoData=${campoData}`),
        api.get(`/categorias`),
        api.get(`/contas`),
        api.get(`/financeiro/saldo`),
      ]);
      setLancs(lancsRes.data.lancamentos);
      setCategorias(catsRes.data.categorias || []);
      setContas(contasRes.data.contas || []);
      setResumo(resumoRes.data);
      setSaldoInicial(saldoRes.data.saldoInicial || 0);
    } catch {} finally { setLoading(false); }
  }, [de, ate, campoData]);

  const definirSaldo = async () => {
    const atual = saldoInicial || 0;
    const entrada = window.prompt("Informe o saldo atual em conta (R$):", atual);
    if (entrada === null) return;
    const valor = Number(String(entrada).replace(/[^0-9,-]/g, "").replace(",", "."));
    if (isNaN(valor)) { alert("Valor inválido"); return; }
    try {
      await api.put("/financeiro/saldo", { saldoInicial: valor, dataSaldo: new Date().toISOString().slice(0,10) });
      setSaldoInicial(valor);
    } catch (e) { alert("Erro ao salvar saldo"); }
  };

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get("/financeiro?de=2000-01-01&ate=2999-12-31&campoData=dataEmissao&limit=100000")
      .then(r => setSemana((r.data.lancamentos||[]).filter(l => l.status !== "cancelado")))
      .catch(()=>{});
  }, []);

  const salvar = async (form) => {
    if (form._deletar) {
      if (!window.confirm("Excluir este lançamento?")) return;
      await api.delete(`/financeiro/${form._id}`);
      setLancs(prev => prev.filter(l => l._id !== form._id));
      setSemana(prev => prev.filter(l => l._id !== form._id));
    } else if (form._id) {
      const { data } = await api.put(`/financeiro/${form._id}`, form);
      setLancs(prev => prev.map(l => l._id === data._id ? data : l));
      setSemana(prev => prev.map(l => l._id === data._id ? data : l));
    } else {
      const { data } = await api.post("/financeiro", form);
      if (data && data.criados) { alert(data.criados + " parcelas recorrentes criadas. Elas caem nos próximos meses — use o filtro De/até (ou o Painel de Gestão) para vê-las."); }
      else { setLancs(prev => [data, ...prev]); setSemana(prev => [data, ...prev]); }
    }
    setModal(null);
    carregar();
  };

  const marcarPago = async (id) => {
    const alvo = lancamentos.find(l=>l._id===id) || semana.find(l=>l._id===id);
    let banco = alvo?.banco;
    if (!banco && contas.length) {
      const lista = contas.map((c,i)=>`${i+1}) ${c.nome}`).join("\n");
      const tipoTxt = alvo?.tipo==="entrada" ? "recebimento entra" : "pagamento sai";
      const esc = window.prompt(`Em qual conta esse ${tipoTxt}? Digite o número:\n\n${lista}`, "1");
      if (esc===null) return;
      const idx = parseInt(esc,10)-1;
      if (idx>=0 && idx<contas.length) banco = contas[idx].nome;
    }
    const { data } = await api.patch(`/financeiro/${id}/pagar`, banco ? { banco } : {});
    setLancs(prev => prev.map(l => l._id === id ? data : l));
    setSemana(prev => prev.map(l => l._id === id ? data : l));
    carregar();
  };

  const filtradas = lancamentos.filter(l => {
    const tipoOk = aba === "entradas" ? l.tipo === "entrada" : aba === "saidas" ? l.tipo === "saida" : true;
    const statusOk = !filtroStatus || l.status === filtroStatus;
    return tipoOk && statusOk;
  });
  const money = (l) => l.dataPagamento || l.dataVencimento || l.dataEmissao || l.createdAt;
  const dstr = (l) => String(money(l)||"").slice(0,10);
  const ehAba = (l) => aba==="entradas" ? l.tipo==="entrada" : l.tipo==="saida";
  const carry = String(de).slice(8,10) === "01";
  const listaExibir = (aba==="entradas"||aba==="saidas")
    ? semana.filter(l => ehAba(l) && (!filtroStatus || l.status===filtroStatus) && (
          (dstr(l) >= de && dstr(l) <= ate) ||
          (carry && ["pendente","vencido"].includes(l.status) && dstr(l) < de)
        )).sort((a,b)=> dstr(a).localeCompare(dstr(b)))
    : filtradas;

  const vencidos = lancamentos.filter(l => l.dataVencimento && String(l.dataVencimento).slice(0,10) < new Date().toLocaleDateString("en-CA") && l.status === "pendente");

  const ABAS = [
    { id:"entradas",  label:"💰 A Receber",    cor:"#10b981" },
    { id:"saidas",    label:"💸 A Pagar",       cor:"#ef4444" },
    { id:"fluxo",     label:"📊 Fluxo de Caixa",cor:ORANGE    },
    { id:"dre",       label:"📋 DRE",           cor:"#6366f1" },
    { id:"dashboard", label:"📈 Dashboard",     cor:"#0ea5e9" },
    { id:"relatorios", label:"📑 Relatórios",   cor:"#8b5cf6" },
  ];

  return (
    <div style={{ fontFamily:"'Inter',-apple-system,sans-serif", background:theme.bg, minHeight:"100vh" }}>
      {/* Header */}
      <div style={{ padding:"20px 28px 0", background:theme.bgSecond, borderBottom:`1px solid ${theme.border}` }}>

        {/* Alertas de vencimento */}
        {vencidos.length > 0 && (
          <div style={{ background:"#ef444415", border:"1px solid #ef444433", borderRadius:10, padding:"10px 16px", marginBottom:14, fontSize:13, color:"#ef4444", display:"flex", alignItems:"center", gap:8 }}>
            ⚠️ <strong>{vencidos.length} lançamento{vencidos.length>1?"s":""}  vencido{vencidos.length>1?"s":""}</strong> — total {fmt(vencidos.reduce((a,l)=>a+l.valor,0))}
          </div>
        )}

        {/* Cards de resumo */}
        {resumo && (
          <div style={{ display:"flex", gap:12, marginBottom:16, flexWrap:"wrap" }}>
            <div onClick={()=>setModalContas(true)} title="Clique para gerenciar contas e saldos" style={{ background:theme.bgCard, border:`2px solid #EE6A1A`, borderRadius:12, padding:"16px 20px", flex:"1.4 1 220px", minWidth:220, cursor:"pointer", boxShadow:theme.name==="light"?"0 1px 4px rgba(0,0,0,.05)":"none" }}>
              <div style={{ fontSize:12, color:theme.textMuted, fontWeight:600, marginBottom:6, display:"flex", alignItems:"center", justifyContent:"space-between" }}>Saldo atual <span style={{ fontSize:11, color:theme.textMuted, fontWeight:400 }}>editar</span></div>
              <div style={{ fontSize:28, fontWeight:800, letterSpacing:"-.02em", color:((resumo.saldoCaixa ?? (saldoInicial + resumo.totalRecebido - resumo.totalPago)))>=0?"#1D9E75":"#E24B4A", fontFamily:"'Inter',sans-serif" }}>{fmt((resumo.saldoCaixa ?? (saldoInicial + resumo.totalRecebido - resumo.totalPago)))}</div>
              <div style={{ fontSize:11, color:theme.textMuted, marginTop:5, lineHeight:1.6 }}>{(resumo.contasSaldo||[]).length===0 ? "cadastre contas em 🏦 Bancos" : (resumo.contasSaldo||[]).map(c=>(<div key={c._id} style={{ display:"flex", justifyContent:"space-between", gap:10 }}><span style={{ display:"flex", alignItems:"center", gap:5, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}><span style={{ width:7, height:7, borderRadius:2, background:c.cor||"#6366f1", display:"inline-block", flexShrink:0 }}/>{c.nome}</span><span style={{ fontWeight:700, color:c.saldo>=0?theme.textSub:"#E24B4A" }}>{fmt(c.saldo)}</span></div>))}</div>
            </div>
            {[
              ["A receber (aberto)", fmt(resumo.aReceber), "#1D9E75", "recebido no mês " + fmt(resumo.totalRecebido), ()=>{ setAba("entradas"); setFiltroStatus("pendente"); }],
              ["A pagar (aberto)", fmt(resumo.aPagar), "#E24B4A", "pago no mês " + fmt(resumo.totalPago), ()=>{ setAba("saidas"); setFiltroStatus("pendente"); }],
              ["Resultado do mês", fmt(resumo.totalEntradas - resumo.totalSaidas), (resumo.totalEntradas - resumo.totalSaidas)>=0?"#1D9E75":"#E24B4A", "receitas − despesas (competência)", null],
            ].map(([label,val,cor,sub,acao])=>(
              <div key={label} onClick={acao||undefined} title={acao?"Clique para filtrar":undefined} style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:"16px 20px", flex:"1 1 150px", minWidth:150, cursor:acao?"pointer":"default", transition:"transform .1s", boxShadow:theme.name==="light"?"0 1px 4px rgba(0,0,0,.05)":"none" }}
                onMouseEnter={e=>{ if(acao) e.currentTarget.style.transform="translateY(-2px)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform="none"; }}>
                <div style={{ fontSize:12, color:theme.textMuted, fontWeight:600, marginBottom:6 }}>{label}</div>
                <div style={{ fontSize:24, fontWeight:800, color:cor, fontFamily:"'Inter',sans-serif" }}>{val}</div>
                <div style={{ fontSize:11, color:theme.textMuted, marginTop:3 }}>{sub}</div>
              </div>
            ))}
          </div>
        )}

        {(() => {
          const _pad = (n) => String(n).padStart(2,"0");
          const hojeD = new Date(); hojeD.setHours(0,0,0,0);
          const fimD = new Date(); fimD.setHours(0,0,0,0); fimD.setDate(fimD.getDate()+7);
          const hojeStr = hojeD.getFullYear()+"-"+_pad(hojeD.getMonth()+1)+"-"+_pad(hojeD.getDate());
          const dref = (l) => l.dataVencimento || l.dataPagamento;
          const naData = (l) => dref(l) && String(dref(l)).slice(0,10) === hojeStr;
          const iniD = new Date(); iniD.setHours(0,0,0,0); iniD.setDate(iniD.getDate()-45);
          const naSemana = (l) => { const r = dref(l); if(!r) return false; const d = new Date(String(r).slice(0,10)+"T12:00:00"); return d >= iniD && d <= fimD; };
          const pend = (l) => ["pendente","vencido"].includes(l.status);
          const somaS = (arr) => arr.reduce((a,l)=>a+Number(l.valor||0),0);
          const subTxt = (arr) => { const p = arr.filter(pend).length; return arr.length + " lançamento" + (arr.length!==1?"s":"") + (p ? " · " + p + " pendente" + (p!==1?"s":"") : " · todos pagos"); };
          const pagHoje = semana.filter(l => l.tipo==="saida" && naData(l));
          const recHoje = semana.filter(l => l.tipo==="entrada" && naData(l));
          const pagSem = semana.filter(l => l.tipo==="saida" && naSemana(l) && pend(l));
          const recSem = semana.filter(l => l.tipo==="entrada" && naSemana(l) && pend(l));
          const mini = [
            ["Saídas hoje", somaS(pagHoje), subTxt(pagHoje), "#E24B4A", ()=>{ setAba("saidas"); setFiltroStatus(""); }],
            ["Entradas hoje", somaS(recHoje), subTxt(recHoje), "#1D9E75", ()=>{ setAba("entradas"); setFiltroStatus(""); }],
            ["A pagar · 7 dias + atraso", somaS(pagSem), pagSem.length + " pendente(s)", "#E24B4A", ()=>{ setAba("saidas"); setFiltroStatus("pendente"); }],
            ["A receber · 7 dias + atraso", somaS(recSem), recSem.length + " pendente(s)", "#1D9E75", ()=>{ setAba("entradas"); setFiltroStatus("pendente"); }],
          ];
          return (
            <div style={{ display:"flex", alignItems:"stretch", background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:"4px 0", marginBottom:16, flexWrap:"wrap" }}>
              {mini.map(([label,val,sub,cor,acao],i) => (
                <div key={label} onClick={acao} title="Ver na lista" style={{ flex:"1 1 170px", minWidth:170, padding:"8px 16px", cursor:"pointer", borderLeft: i>0 ? `1px solid ${theme.border}` : "none" }}>
                  <div style={{ fontSize:10.5, color:theme.textMuted, fontWeight:600, textTransform:"uppercase", letterSpacing:.3 }}>{label}</div>
                  <div style={{ fontSize:15, fontWeight:800, color:cor, marginTop:3 }}>{fmt(val)} <span style={{ fontSize:10, color:theme.textMuted, fontWeight:400 }}>· {sub}</span></div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Toolbar */}
        <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:16, flexWrap:"wrap" }}>
          {/* Abas */}
          <div style={{ display:"flex", background:theme.bgInput, borderRadius:10, padding:3, gap:2 }}>
            {ABAS.map(a=>(
              <button key={a.id} onClick={()=>setAba(a.id)} style={{ padding:"7px 14px", borderRadius:8, fontSize:12, fontWeight:aba===a.id?700:400, cursor:"pointer", background:aba===a.id?a.cor:"transparent", border:"none", color:aba===a.id?"var(--card-bg)":theme.textMuted, transition:"all .15s" }}>{a.label}</button>
            ))}
          </div>

          {/* Período */}
          <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
            <div style={{ display:"flex", background:theme.bgInput, borderRadius:8, padding:2, gap:2, marginRight:8 }}>{[["dataEmissao","Emissão"],["dataPagamento","Pagamento"]].map(([val,lbl])=>(<button key={val} onClick={()=>setCampoData(val)} style={{ padding:"5px 10px", borderRadius:6, fontSize:11, fontWeight:campoData===val?700:400, cursor:"pointer", background:campoData===val?ORANGE:"transparent", border:"none", color:campoData===val?"var(--card-bg)":theme.textMuted, whiteSpace:"nowrap" }}>{lbl}</button>))}</div>
            <button onClick={() => { const d=new Date(); irMes(d.getFullYear(), d.getMonth()); }} style={{ padding:"6px 12px", fontSize:12, fontWeight:600, borderRadius:8, cursor:"pointer", border:`1px solid ${theme.border}`, background:theme.bgCard, color:theme.textMuted }}>Mês atual</button>
            <div style={{ display:"flex", alignItems:"center", gap:4, marginRight:6 }}>
              <button onClick={() => passoMes(-1)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:20, color:ORANGE, lineHeight:1, padding:"0 4px" }}>‹</button>
              <span style={{ fontSize:13, fontWeight:700, color:theme.text, textTransform:"capitalize", minWidth:120, textAlign:"center" }}>{rotuloMes}</span>
              <button onClick={() => passoMes(1)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:20, color:ORANGE, lineHeight:1, padding:"0 4px" }}>›</button>
            </div>
            <span style={{ fontSize:11, color:theme.textMuted, marginLeft:4 }}>ou por data:</span>
            <span style={{ fontSize:12, color:theme.textMuted }}>De</span>
            <input type="date" value={de} onChange={e=>setDe(e.target.value)}
              style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:8, padding:"6px 10px", color:theme.text, fontSize:12, outline:"none", colorScheme:theme.name }}/>
            <span style={{ fontSize:12, color:theme.textMuted }}>até</span>
            <input type="date" value={ate} onChange={e=>setAte(e.target.value)}
              style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:8, padding:"6px 10px", color:theme.text, fontSize:12, outline:"none", colorScheme:theme.name }}/>
          </div>

          {/* Filtro status */}
          {(aba==="entradas"||aba==="saidas") && (
            <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)}
              style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:8, padding:"7px 10px", color:theme.text, fontSize:12, outline:"none", colorScheme:theme.name }}>
              <option value="">Todos os status</option>
              {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          )}

          <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
            <button onClick={()=>setModalCategorias(true)} style={{ background:"#0ea5e922", border:"1px solid #0ea5e944", borderRadius:10, padding:"8px 16px", color:"#0ea5e9", fontWeight:700, fontSize:13, cursor:"pointer" }}>⚙️ Categorias</button>
            <button onClick={()=>setModalContas(true)} style={{ background:"#6366f122", border:"1px solid #6366f144", borderRadius:10, padding:"8px 16px", color:"#6366f1", fontWeight:700, fontSize:13, cursor:"pointer" }}>🏦 Bancos</button>
            <button onClick={()=>{ setTipoModal("entrada"); setModal({}); }}
              style={{ background:"#10b98122", border:"1px solid #10b98144", borderRadius:10, padding:"8px 16px", color:"#10b981", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              + Entrada
            </button>
            <button onClick={()=>{ setTipoModal("saida"); setModal({}); }}
              style={{ background:"#ef444422", border:"1px solid #ef444444", borderRadius:10, padding:"8px 16px", color:"#ef4444", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              + Saída
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div style={{ padding:"20px 28px" }}>
        {loading ? (
          <div style={{ textAlign:"center", padding:60, color:theme.textMuted }}>Carregando...</div>
        ) : (
          <>
            {(aba==="entradas"||aba==="saidas") && (
              <TabelaLancamentos lancamentos={listaExibir} onEdit={l=>{ setTipoModal(l.tipo); setModal(l); }} onPagar={marcarPago} tipo={aba==="entradas"?"entrada":"saida"}/>
            )}
            {aba==="fluxo" && (
              <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                <GraficoFluxo dados={resumo?.fluxoMensal} theme={theme}/>
                {/* Categorias de despesa */}
                {resumo?.categorias && Object.keys(resumo.categorias).length > 0 && (
                  <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:20 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:14 }}>💸 Despesas por categoria</div>
                    {Object.entries(resumo.categorias).sort((a,b)=>b[1]-a[1]).map(([cat,val],i)=>{
                      const maxCat = Math.max(...Object.values(resumo.categorias));
                      const cores = ["#6366f1","#ef4444","#f59e0b","#3b82f6","#e879f9","#10b981","#8E8E93"];
                      const cor = cores[i%cores.length];
                      return (
                        <div key={cat} style={{ marginBottom:10 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:5 }}>
                            <span style={{ color:theme.textSub }}>{cat}</span>
                            <span style={{ color:cor, fontWeight:700 }}>{fmt(val)}</span>
                          </div>
                          <div style={{ background:theme.bgInput, borderRadius:99, height:8, overflow:"hidden" }}>
                            <div style={{ width:`${(val/maxCat)*100}%`, background:cor, height:"100%", borderRadius:99 }}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {aba==="dre" && <DREMensal de={de} ate={ate} theme={theme}/>}
            {aba==="dashboard" && <DashboardFinanceiro resumo={resumo} theme={theme}/>}
            {aba==="relatorios" && <RelatorioFinanceiro theme={theme} categorias={categorias} contas={contas} clientes={[...new Set(lancamentos.map(l=>l.clienteNome).filter(Boolean))].sort()}/>}
          </>
        )}
      </div>

      {modalCategorias && <ModalCategorias onClose={()=>setModalCategorias(false)} />}
      {modalContas && <ModalContas onClose={()=>setModalContas(false)} />}
      {modal !== null && (
        <ModalLancamento
          lanc={Object.keys(modal).length ? modal : null}
          tipo={tipoModal}
          onClose={()=>setModal(null)}
          onSave={salvar}
          categorias={categorias}
          contas={contas}
          clientes={[]}
        />
      )}
    </div>
  );
}
